import { Badge } from "@/components/Badge";
import i18n from "@/i18n";
import { DownloadButton } from "@/components/DownloadButton";
import { ExportStatus } from "@/lib/utils/exportStatus";
import { statusToBadgeColor } from "@/lib/utils/statusTobadgeColor";
import { parseDateWithHours } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Clients } from "@/lib/clients";
import { ClientTables } from "@/lib/clientTables";
import { Link } from "react-router-dom";

export type S1DataUploadRow = {
  id: string;
  file_name: string;
  user_name: string;
  status: string;
  provider_config_name: string;
  created_at: string;
  updated_at: string;
};

export const s1_data_upload_columns: ColumnDef<S1DataUploadRow>[] = [
  {
    accessorKey: "id",
    header: i18n.t(`${ClientTables.S1_DATA_UPLOAD}.columns.id`, {
      ns: "tables",
      defaultValue: "ID",
    }),
  },
  {
    accessorKey: "file_name",
    header: i18n.t(`${ClientTables.S1_DATA_UPLOAD}.columns.file_name`, {
      ns: "tables",
      defaultValue: "File name",
    }),
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return (
        <span className="whitespace-nowrap overflow-hidden text-ellipsis block">
          {value}
        </span>
      );
    },
  },
  {
    accessorKey: "user_name",
    header: i18n.t(`${ClientTables.S1_DATA_UPLOAD}.columns.user_name`, {
      ns: "tables",
      defaultValue: "User",
    }),
  },
  {
    accessorKey: "status",
    header: i18n.t(`${ClientTables.S1_DATA_UPLOAD}.columns.status`, {
      ns: "tables",
      defaultValue: "Status",
    }),
    cell: ({ getValue }) => {
      const value = getValue() as ExportStatus;
      const badgeColor = statusToBadgeColor[value] || "error";
      return <Badge color={badgeColor} />;
    },
  },
  {
    accessorKey: "provider_config_name",
    header: i18n.t(`${ClientTables.S1_DATA_UPLOAD}.columns.provider_config_name`, {
      ns: "tables",
      defaultValue: "Configuration",
    }),
  },
  {
    accessorKey: "created_at",
    header: i18n.t(`${ClientTables.S1_DATA_UPLOAD}.columns.created_at`, {
      ns: "tables",
      defaultValue: "Created at",
    }),
    cell: ({ row }) => {
      const cell_data: string | undefined = row.getValue("created_at");
      if (cell_data) {
        return parseDateWithHours(cell_data);
      }
      return "";
    },
  },
  {
    accessorKey: "updated_at",
    header: i18n.t(`${ClientTables.S1_DATA_UPLOAD}.columns.updated_at`, {
      ns: "tables",
      defaultValue: "Updated at",
    }),
    cell: ({ row }) => {
      const cell_data: string | undefined = row.getValue("updated_at");
      if (cell_data) {
        return parseDateWithHours(cell_data);
      }
      return "";
    },
  },
  {
    accessorKey: "download",
    header: i18n.t(`${ClientTables.S1_DATA_UPLOAD}.columns.download`, {
      ns: "tables",
      defaultValue: "Download",
    }),
    cell: ({ row }) => {
      const id = row.getValue("id") as string;
      const fileName = row.getValue("file_name") as string;
      const href = `${
        import.meta.env.VITE_BACKEND_URL
      }/${Clients.S1}/${ClientTables.S1_DATA_UPLOAD}/download/${id}`;

      return (
        <div className="flex flex-row items-center justify-between w-full">
          <DownloadButton
            href={href}
            filename={`${fileName}`}
            size="sm"
            variant="light"
            color="primary"
            isIconOnly
          />
          <div className="flex justify-end">
            <Link
              to={String(id)}
              className="text-xs hover:underline"
            >
              {i18n.t("actions.details")}
            </Link>
          </div>
        </div>
      );
    },
  },
];
